import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Notification, NotificationPriority } from '../types';
import { markAsRead } from '../lib/notificationService';
import { getNotificationTemplate } from '../lib/notificationTemplates';

interface NotificationCardProps {
    notification: Notification;
    onRead?: (notificationId: string) => void;
    onAction?: (action: string, notificationId: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
    notification,
    onRead,
    onAction,
}) => {
    const navigate = useNavigate();

    const priorityBorderColors = {
        LOW: 'border-l-gray-400',
        MEDIUM: 'border-l-blue-500',
        HIGH: 'border-l-orange-500',
        CRITICAL: 'border-l-red-600',
    };

    const priorityBgColors = {
        LOW: 'bg-gray-50',
        MEDIUM: 'bg-blue-50',
        HIGH: 'bg-orange-50',
        CRITICAL: 'bg-red-50',
    };

    const handleClick = async () => {
        if (!notification.isRead) {
            await markAsRead(notification.id);
            onRead?.(notification.id);
        }

        // Navigate based on entity type
        if (notification.relatedEntityType && notification.relatedEntityId) {
            switch (notification.relatedEntityType) {
                case 'listing':
                    navigate(`/listing/${notification.relatedEntityId}`);
                    break;
                case 'order':
                    navigate(`/dashboard?tab=orders`);
                    break;
                case 'message':
                    navigate(`/messages`);
                    break;
                case 'user':
                    navigate(`/profile/${notification.relatedEntityId}`);
                    break;
            }
        } else if (notification.richContent?.ctaUrl) {
            navigate(notification.richContent.ctaUrl);
        }
    };

    const handleAction = (action: string) => {
        onAction?.(action, notification.id);
    };

    const formatTimeAgo = (timestamp: number) => {
        const seconds = Math.floor((Date.now() - timestamp) / 1000);
        if (seconds < 60) return 'Az önce';
        if (seconds < 3600) return `${Math.floor(seconds / 60)} dk önce`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} sa önce`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)} gün önce`;
        return new Date(timestamp).toLocaleDateString('tr-TR');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)', x: 4 }}
            onClick={handleClick}
            className={`
                p-5
                ${notification.isRead
                    ? 'bg-white dark:bg-gray-950 opacity-60'
                    : 'bg-white dark:bg-gray-900 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] dark:shadow-none'}
                cursor-pointer
                transition-all duration-300
                border-b border-gray-50 dark:border-white/5
                group
            `}
        >
            <div className="flex items-start gap-3">
                {/* Avatar */}
                {(notification.actorAvatar || notification.actorName) && (
                    <div className="flex-shrink-0">
                        {notification.actorAvatar ? (
                            <img
                                src={notification.actorAvatar}
                                alt={notification.actorName || 'User'}
                                className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-800 shadow"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-brand-terracotta/10 text-brand-terracotta flex items-center justify-center font-bold text-xs border-2 border-white dark:border-gray-800 shadow">
                                {notification.actorName?.[0]?.toUpperCase() || 'U'}
                            </div>
                        )}
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${notification.priority === 'HIGH' || notification.priority === 'CRITICAL' ? 'bg-brand-terracotta' : 'bg-blue-500'} ${notification.isRead ? 'hidden' : 'block'}`}></span>
                        <h4 className={`text-sm font-black tracking-tight ${notification.isRead ? 'text-brand-clay/50 dark:text-gray-500' : 'text-brand-clay dark:text-white'}`}>
                            {notification.title}
                        </h4>
                    </div>

                    <p className={`text-[13px] leading-relaxed mb-3 ${notification.isRead ? 'text-brand-clay/40 dark:text-gray-600' : 'text-brand-clay/70 dark:text-gray-400'}`}>
                        {notification.message}
                    </p>

                    {/* Rich content image */}
                    {notification.richContent?.imageUrl && (
                        <div className="relative w-full h-32 rounded-2xl overflow-hidden mb-3 border border-gray-100">
                            <img
                                src={notification.richContent.imageUrl}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Action buttons */}
                    {notification.richContent?.actionButtons && notification.richContent.actionButtons.length > 0 && (
                        <div className="flex gap-2 mb-3">
                            {notification.richContent.actionButtons.map((btn, idx) => (
                                <button
                                    key={idx}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAction(btn.action);
                                    }}
                                    className={`
                                        px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95
                                        ${btn.variant === 'primary' ? 'bg-brand-terracotta text-white shadow-lg shadow-brand-terracotta/20 hover:bg-brand-terracotta/90' : ''}
                                        ${btn.variant === 'secondary' ? 'bg-gray-100 dark:bg-gray-800 text-brand-clay dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700' : ''}
                                        ${btn.variant === 'danger' ? 'bg-red-500 text-white shadow-lg shadow-red-500/20 hover:bg-red-600' : ''}
                                    `}
                                >
                                    {btn.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* CTA button or link */}
                    {notification.richContent?.ctaText && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick();
                            }}
                            className="text-[11px] font-black text-brand-terracotta uppercase tracking-[0.1em] hover:text-brand-clay dark:hover:text-white transition-all block mb-3"
                        >
                            {notification.richContent.ctaText} →
                        </button>
                    )}

                    {/* Timestamp */}
                    <p className="text-[10px] font-bold text-brand-clay/30 dark:text-gray-600 uppercase tracking-widest">
                        {formatTimeAgo(notification.createdAt)}
                    </p>
                </div>

                {/* Unread indicator */}
                {!notification.isRead && (
                    <div className="flex-shrink-0 pt-1">
                        <div className="w-2.5 h-2.5 bg-brand-terracotta rounded-full shadow-[0_0_12px_rgba(195,107,79,0.5)]" />
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default NotificationCard;
